/**
 * Nested T-Hangar (Interleaving/Zipper) Template Parameters
 *
 * Two T-hangar rows with tails interlocking in a shared middle zone.
 * Row A: N units all facing south. Row B: N-1 units all facing north, shifted by half-pitch.
 * No spine wall — the rows physically interleave like a zipper.
 */

import { z } from 'zod'
import type { BaseTemplateParams, DoorType, DoorSpec, UnitType } from '../types'
import { tHangarUnitOverrideSchema } from '../standard-t-hangar/params'

// Re-export shape-level helpers (not layout-level) from standard T-hangar
export { calculateStepWidth, createDefaultTHangarDoorSpec, calculateTShapeArea } from '../standard-t-hangar/params'

// ============================================================================
// END-SPACE MODE
// ============================================================================

/** How to handle the rectangular gaps at both ends of the Row B wing zone */
export type EndSpaceMode = 'separate' | 'merge_row_a' | 'merge_row_b'

// ============================================================================
// NESTED-SPECIFIC HELPERS
// ============================================================================

/**
 * Pitch for same-direction adjacent units (NOT the standard alternating pitch).
 * All units in a row face the same way, so they're spaced unit_width + wall apart.
 */
export function calculateNestedUnitPitch(params: { unit_width_ft: number; wall_thickness_ft: number }): number {
  return params.unit_width_ft + params.wall_thickness_ft
}

// ============================================================================
// ZOD SCHEMA
// ============================================================================

export const nestedTHangarParamsSchema = z.object({
  // Base params
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required').max(10, 'Code too long'),
  latitude: z.number(),
  longitude: z.number(),
  bearing_deg: z.number().transform(val => ((val % 360) + 360) % 360),
  floor_color: z.string(),
  wall_color: z.string(),

  // T-shape dimensions
  unit_width_ft: z.number()
    .min(20, 'Unit width must be at least 20 ft')
    .max(80, 'Unit width must be at most 80 ft'),
  wing_depth_ft: z.number()
    .min(10, 'Wing depth must be at least 10 ft')
    .max(60, 'Wing depth must be at most 60 ft'),
  tail_depth_ft: z.number()
    .min(10, 'Tail depth must be at least 10 ft')
    .max(60, 'Tail depth must be at most 60 ft'),
  tail_width_ft: z.number()
    .min(10, 'Tail width must be at least 10 ft')
    .max(60, 'Tail width must be at most 60 ft'),
  height_ft: z.number()
    .min(8, 'Height must be at least 8 ft')
    .max(30, 'Height must be at most 30 ft'),

  // Unit count (Row A). Row B = unit_count - 1 (always).
  unit_count: z.number().int()
    .min(2, 'At least 2 units required')
    .max(30, 'Max 30 units'),

  // Door config
  default_door_type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
  default_door_width_ft: z.number()
    .min(10, 'Door width must be at least 10 ft')
    .max(80, 'Door width must be at most 80 ft'),
  default_door_height_ft: z.number()
    .min(8, 'Door height must be at least 8 ft')
    .max(30, 'Door height must be at most 30 ft'),

  // Wall thickness
  wall_thickness_ft: z.number().min(0.25).max(2).default(0.5),

  // End-space mode: per-side control for gaps at Row B wing-zone ends
  left_end_mode: z.enum(['separate', 'merge_row_a', 'merge_row_b']).default('separate'),
  right_end_mode: z.enum(['separate', 'merge_row_a', 'merge_row_b']).default('separate'),

  // Per-unit overrides
  row_a_overrides: z.array(tHangarUnitOverrideSchema).default([]),
  row_b_overrides: z.array(tHangarUnitOverrideSchema).default([]),
}).refine(
  (data) => data.tail_width_ft < data.unit_width_ft,
  { message: 'Tail width must be less than unit width', path: ['tail_width_ft'] }
).refine(
  (data) => Math.abs(data.tail_width_ft - data.unit_width_ft / 2) < 0.01,
  { message: 'Tail width must be exactly half of unit width for nested interleaving', path: ['tail_width_ft'] }
)

// ============================================================================
// TYPES
// ============================================================================

export interface NestedTHangarParams extends BaseTemplateParams {
  unit_width_ft: number
  wing_depth_ft: number
  tail_depth_ft: number
  tail_width_ft: number
  height_ft: number
  unit_count: number // Row A count; Row B = unit_count - 1
  left_end_mode: EndSpaceMode
  right_end_mode: EndSpaceMode
  default_door_type: DoorType
  default_door_width_ft: number
  default_door_height_ft: number
  wall_thickness_ft: number
  row_a_overrides: Array<{
    index: number
    type?: UnitType
    doors?: { front?: DoorSpec[] }
  }>
  row_b_overrides: Array<{
    index: number
    type?: UnitType
    doors?: { front?: DoorSpec[] }
  }>
}

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULT_NESTED_T_HANGAR_VALUES: NestedTHangarParams = {
  name: 'New Nested T-Hangar',
  code: 'NTH',
  latitude: 0,
  longitude: 0,
  bearing_deg: 0,
  floor_color: '#808080',
  wall_color: '#A0A0A0',

  unit_width_ft: 44,
  wing_depth_ft: 21,
  tail_depth_ft: 21,
  tail_width_ft: 22,
  height_ft: 14,

  unit_count: 10,
  left_end_mode: 'separate',
  right_end_mode: 'separate',

  default_door_type: 'rolling',
  default_door_width_ft: 40,
  default_door_height_ft: 12,
  wall_thickness_ft: 0.5,

  row_a_overrides: [],
  row_b_overrides: [],
}
